import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import chunk from 'lodash/chunk';
import times from 'lodash/times';
import { connect } from 'react-redux';

import DisplayComponent from 'formlabs-web-components/BaseComponents/DisplayComponent';
import Header from 'formlabs-web-components/Type/Header';
import { Grid, Cell } from 'formlabs-web-components/Grid';
import GenericCarousel from './GenericCarousel';
import ProductCard from '../../../content/ProductCard';

import playIcon from '../../../../../../public/ic-play.svg';
import pauseIcon from '../../../../../../public/ic-pause.svg';
import productLoadingAnimation from '../../../../../../public/product-loading.svg';
import './ProductCarousel.module.css';
import useFetchProductsBySku from '../../../../hooks/useFetchProductsBySku';
import useIsMobile from '../../../../hooks/useIsMobile';

const ProductCarouselFrame = ({
  children,
  autoplay,
  stretchChildren,
  fullHeight,
  draggable,
  customClasses,
  name,
  tagline,
  horizontalLine,
  disableControls
}) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentDirection, setCurrentDirection] = useState(1);
  const [isSliding, setIsSliding] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoplay);

  const isMobile = useIsMobile(640);

  return (
    <div
      styleName={classNames('carousel-container', {
        'full-height': fullHeight
      })}
      className={customClasses}
    >
      {name && (
        <Header level={2} displayAs={4} styleName="name">
          {name}
        </Header>
      )}
      <div styleName={classNames('controls', 'top')}>
        {tagline && <p styleName="tagline">{tagline}</p>}
        {!disableControls && (
          <div style={{ marginTop: isMobile ? '1rem' : '0rem' }}>
            {autoplay && (
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                aria-label={
                  isPlaying ? t('Pause slideshow') : t('Play slideshow')
                }
                styleName={classNames({ play: !isPlaying, pause: isPlaying })}
              >
                <img
                  src={isPlaying ? pauseIcon : playIcon}
                  styleName="icon"
                  alt=""
                  role="presentation"
                />
              </button>
            )}
            <button
              styleName="prev"
              aria-label={t('Show previous slide')}
              onClick={e => {
                e.stopPropagation();
                if (!isSliding) {
                  setCurrentSlide(currentSlide - 1);
                  setCurrentDirection(-1);
                }
              }}
            >
              <i className="fl-navigate_before" />
            </button>
            <button
              styleName="next"
              aria-label={t('Show next slide')}
              onClick={e => {
                e.stopPropagation();
                if (!isSliding) {
                  setCurrentSlide(currentSlide + 1);
                  setCurrentDirection(1);
                }
              }}
            >
              <i className="fl-navigate_next" />
            </button>
          </div>
        )}
      </div>
      {horizontalLine && <hr styleName="line" />}
      <GenericCarousel
        stretchChildren={stretchChildren}
        fullHeight={fullHeight}
        draggable={draggable}
        initialSlide={0}
        nextSlide={currentSlide}
        nextDirection={currentDirection}
        onSlideBegin={nextSlide => {
          setCurrentSlide(nextSlide);
          setIsSliding(true);
        }}
        onSlideEnd={() => {
          setIsSliding(false);
        }}
        autoplaying={isPlaying}
        autoplayInterval={5000}
      >
        {children}
      </GenericCarousel>
    </div>
  );
};

ProductCarouselFrame.propTypes = {
  customClasses: PropTypes.node,
  children: PropTypes.node
};

const ProductCarousel = ({
  skus,
  baseUrl,
  children,
  autoplay,
  stretchChildren,
  fullHeight,
  draggable,
  customClasses,
  name,
  tagline,
  horizontalLine,
  storeId,
  vertical
}) => {
  const { loading, data } = useFetchProductsBySku(storeId, skus, vertical);
  const isMobile = useIsMobile(640);
  const isMedium = useIsMobile(1024);
  const chunkSize = isMobile ? 1 : isMedium ? 2 : 4;

  if (loading || !data) {
    return (
      <ProductCarouselFrame
        disableControls={true}
        autoplay={autoplay}
        fullHeight={fullHeight}
        draggable={draggable}
        customClasses={customClasses}
        name={name}
        tagline={tagline}
        horizontalLine={horizontalLine}
      >
        <Grid direction="x" gutters="margin">
          {times(chunkSize, index => {
            return (
              <Cell
                styles={{
                  marginBottom: isMobile ? '5px' : isMedium ? '2rem' : '0rem'
                }}
                key={index}
                full={stretchChildren}
                largeSize={6}
                mediumSize={12}
              >
                <div
                  style={{
                    height: '300px',
                    backgroundImage: `url(${productLoadingAnimation})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </Cell>
            );
          })}
        </Grid>
      </ProductCarouselFrame>
    );
  }

  const chunked = chunk(data, chunkSize);
  const disableControls = chunked.length < 2;

  return (
    <ProductCarouselFrame
      disableControls={disableControls}
      autoplay={autoplay}
      fullHeight={fullHeight}
      draggable={draggable && !disableControls}
      customClasses={customClasses}
      name={name}
      tagline={tagline}
      horizontalLine={horizontalLine}
    >
      {chunked.map((value, index) => (
        <Grid key={index} direction="x" gutters="margin">
          {value.map((value, index) => {
            return (
              <Cell
                key={index}
                marginBottom={true}
                full={stretchChildren}
                largeSize={6}
                mediumSize={12}
                backgroundColor="white"
              >
                <ProductCard {...value} hover="none" baseUrl={baseUrl} />
              </Cell>
            );
          })}
        </Grid>
      ))}
    </ProductCarouselFrame>
  );
};

export default connect(state => {
  return { storeId: state.store.id };
})(DisplayComponent(ProductCarousel));
